import {
  FdcNotificationEventsQuery,
  FdcNotificationEventsQueryResult,
  useFdcNotificationEventsQuery,
} from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { createStrictContext } from 'utils/createContext'
import {
  UseRecipientsFn,
  useRecipientsFn,
} from 'components/CIMSNotifications/hooks/useRecipientsFn'
import {
  FDCNotificationEvent,
  FDCNotificationEventItem,
} from '@cims-online/shared'
import { useProjectContext } from 'context/ProjectContext'

type Query = FdcNotificationEventsQuery
export type FDCNotification = Query['fdcNotifications']['records'][0]
export type Group = Omit<
  Query['fdcApprovalGroups']['records'][0],
  '__typename'
> &
  Omit<Query['fdcConcurrenceGroups']['records'][0], '__typename'>

interface FDCNotificationsProviderContext
  extends Pick<FdcNotificationEventsQueryResult, 'refetch'> {
  data: {
    fdcNotifications: Query['fdcNotifications']['records']
    fdcConcurrenceLevels: Query['fdcConcurrenceLevels']['records']
    fdcConcurrenceGroups: Query['fdcConcurrenceGroups']['records']
    fdcApprovalGroups: Query['fdcApprovalGroups']['records']
  }
  fetching: FdcNotificationEventsQueryResult['loading']
  recipients: UseRecipientsFn<FDCNotification['recipients']>
  concurrerEvents: Readonly<FDCNotificationEventItem>[]
  approverEvents: Readonly<FDCNotificationEventItem>[]
}

const { Provider, useContextFn } =
  createStrictContext<FDCNotificationsProviderContext>(
    'FDCNotificationsContext'
  )

export const useFDCNotificationsContext = useContextFn

const FDCNotificationsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    data,
    loading: fetching,
    refetch,
  } = useFdcNotificationEventsQuery({
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  })
  const recipients = useRecipientsFn({
    data,
    getter: (id, { fdcNotifications }) =>
      fdcNotifications.records.find(
        ({ notificationEventId }) => notificationEventId === id
      )?.recipients,
    fallback: {
      __typename: 'FDCNotificationRecipients',
      every: null,
      byApprovalGroup: [],
      byConcurrenceGroup: [],
      byConcurrenceLevel: [],
      byConcurrenceLevelAndApprovalGroup: [],
      byConcurrenceLevelAndConcurrenceGroup: [],
    },
  })
  const concurrerEvents = [
    FDCNotificationEvent.Submitted,
    FDCNotificationEvent.ConcurrenceLevelRejected,
  ]
  const approverEvents = [FDCNotificationEvent.PendingApprovals]
  return (
    <Provider
      value={{
        data: {
          fdcNotifications: data?.fdcNotifications.records ?? [],
          fdcConcurrenceLevels: data?.fdcConcurrenceLevels.records ?? [],
          fdcConcurrenceGroups: data?.fdcConcurrenceGroups.records ?? [],
          fdcApprovalGroups: data?.fdcApprovalGroups.records ?? [],
        },
        fetching,
        refetch,
        recipients,
        concurrerEvents,
        approverEvents,
      }}
    >
      {children}
    </Provider>
  )
}

export default FDCNotificationsProvider
